import type { PixelConfig } from "./types";

export const PXL_NATIVE_CAPABILITIES = [
  "event_collection",
  "consent_governance",
  "first_party_identity",
  "identity_graph",
  "visitor_profiles",
  "account_matching",
  "audience_segmentation",
  "intent_scoring",
  "journey_detection",
  "attribution",
  "experimentation",
  "conversion_measurement",
  "activation_dispatch",
  "data_rights",
] as const;

export type PxlNativeCapability = (typeof PXL_NATIVE_CAPABILITIES)[number];

const ADDON_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/i;
const ADDON_ALIASES: Record<string, string> = {
  google_ads: "google",
  microsoft_ads: "microsoft",
  x_ads: "x",
};

export function normalizeAddonName(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  if (!ADDON_PATTERN.test(normalized)) return undefined;
  return ADDON_ALIASES[normalized] ?? normalized;
}

export function sanitizeAddonAllowlist(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .slice(0, 64)
        .map(normalizeAddonName)
        .filter((name): name is string => Boolean(name)),
    ),
  );
}

export function isFirstPartyProvider(provider: string): boolean {
  const normalized = provider.trim().toLowerCase();
  return normalized === "prospix" || normalized.startsWith("prospix_");
}

export function isOptionalAddonEnabled(
  config: Pick<PixelConfig, "platform">,
  provider: string,
): boolean {
  if (config.platform.mode !== "hybrid") return false;
  const normalizedProvider = normalizeAddonName(provider);
  if (!normalizedProvider) return false;
  return config.platform.addons.enabled.some(
    (configured) =>
      normalizedProvider === configured ||
      normalizedProvider.startsWith(`${configured}_`),
  );
}

export function canAcceptProvider(
  config: Pick<PixelConfig, "platform">,
  provider: string,
): boolean {
  return (
    isFirstPartyProvider(provider) || isOptionalAddonEnabled(config, provider)
  );
}

export const canAcceptIdentityProvider = canAcceptProvider;

const isTrustedAtdataCollectUrl = (raw: string | undefined) => {
  if (!raw) return false;
  try {
    const parsed = new URL(raw);
    const hostname = parsed.hostname.toLowerCase();
    return (
      parsed.protocol === "https:" &&
      ["atdata.com", "towerdata.com"].some(
        (trusted) => hostname === trusted || hostname.endsWith(`.${trusted}`),
      )
    );
  } catch {
    return false;
  }
};

export function isBrowserDestinationReady(
  config: Pick<PixelConfig, "destinations">,
  destination: string,
) {
  const browser =
    typeof window === "undefined" ? undefined : (window as unknown as any);
  switch (destination) {
    case "meta":
      return typeof browser?.fbq === "function";
    case "google":
      return typeof browser?.gtag === "function";
    case "tiktok":
      return typeof browser?.ttq?.track === "function";
    case "linkedin":
      return typeof browser?.lintrk === "function";
    case "microsoft":
      return typeof browser?.uetq?.push === "function";
    case "pinterest":
      return typeof browser?.pintrk === "function";
    case "snap":
      return typeof browser?.snaptr === "function";
    case "x":
      return typeof browser?.twq === "function";
    case "reddit":
      return typeof browser?.rdt === "function";
    case "atdata": {
      const destinationConfig = config.destinations.config.atdata ?? {};
      return (
        Boolean(destinationConfig.site_id || destinationConfig.pixel_id) &&
        isTrustedAtdataCollectUrl(destinationConfig.collect_url)
      );
    }
    default:
      return false;
  }
}

export function getPlatformStatus(config: PixelConfig) {
  const enabledAddons = sanitizeAddonAllowlist(config.platform.addons.enabled);
  const enabledDestinations = sanitizeAddonAllowlist(
    config.destinations.enabled,
  ).filter((destination) => isOptionalAddonEnabled(config, destination));

  const activeBrowserDestinations = enabledDestinations.filter((destination) =>
    isBrowserDestinationReady(config, destination),
  );

  return {
    mode: config.platform.mode,
    nativeCapabilities: [...PXL_NATIVE_CAPABILITIES],
    optionalAddons: enabledAddons,
    configuredBrowserDestinations: enabledDestinations,
    activeBrowserDestinations,
    unavailableBrowserDestinations: enabledDestinations.filter(
      (destination) => !activeBrowserDestinations.includes(destination),
    ),
    thirdPartyCodeAllowed:
      config.platform.mode === "hybrid" &&
      activeBrowserDestinations.some((destination) => destination !== "atdata"),
  };
}
